// AI 教练的「页面入口」注册表：页面组件注册「本页面如何打开教练」，
// 浮窗（全局组件，挂在 App.vue）据此做两件事：
//   1. 检测用户是否已经离开打开教练时的页面（上下文可能已与当前页面脱节）；
//   2. 提供「改用当前页面」：用页面的 openOpts 重新绑定上下文，与页面自己的按钮行为一致。
//
// entry_page 的取值就是路由 name（如 problem-detail）：它同时是统计口径
// （后端 ai_conversation.entry_page 按会话聚合出「用户从哪里问起」的分布）。
// 与之配套的 entry_path 由 BuildAiCoachEntryPath 生成（见文件末尾）：真实访问路径 + 查询参数名。
//
// 注意：同一个页面组件可能服务多个路由（如 ProblemDetail 同时是 problem-detail /
// problem-daily-detail / contest-problem-detail），因此页面必须按**当前路由 name** 注册与上报，
// 写死其中一个会把其它路由的使用量并进同一桶。

import { ref } from "vue";
import type { LocationQuery, RouteRecordName } from "vue-router";
import type { AiCoachRef } from "@/stores/aiCoach.ts";

/** 一个可打开教练的页面入口 */
export interface AiCoachPageEntry {
  /** 路由 name（与统计的 entry_page 同值） */
  key: string;
  /** 展示名（中文，用于浮窗提示与统计映射） */
  title: string;
  /** 本页面对应的教练场景 */
  scene: string;
  /** 场景绑定对象 */
  ref: AiCoachRef;
  /**
   * 额外打开参数：由页面按自己的状态计算（如提交详情页的禁用原因、预填关键字）。
   * 浮窗「改用当前页面」时调用它，与页面自己的入口按钮行为完全一致。
   */
  openOpts?: () => { notice?: string; promptKeywords?: string; draft?: string };
}

/** 已注册的页面入口（页面组件挂载时注册、卸载时注销；同一路由只保留一个） */
const pageEntries = new Map<string, AiCoachPageEntry>();

/**
 * 注册表版本：注册 / 注销时自增。
 * 同一路由下参数变化（题目 A → 题目 B）时 route.fullPath 会变、但注册表要等页面
 * 重新拉取数据后才更新——浮窗同时监听它，保证「页面数据加载完」也会触发一次漂移判定。
 */
export const aiCoachEntryVersion = ref(0);

/**
 * 常用页面的展示名（浮窗提示与统计页共用）。
 * 未覆盖的 key 原样展示：页面会新增，这里只覆盖高频入口，避免表格追着路由表跑。
 */
const PAGE_TITLES: Record<string, string> = {
  "problem-list": "题目列表",
  "problem-detail": "题目详情",
  "problem-daily-detail": "每日一题详情",
  "problem-statistics": "题目统计",
  "problem-recommend": "题目推荐",
  "problem-daily-list": "每日一题",
  "judge-list": "评测状态",
  "judge-detail": "提交详情",
  "contest-list": "比赛列表",
  "contest-detail": "比赛详情",
  "contest-problem-detail": "比赛题目",
  "contest-judge-detail": "比赛提交详情",
  "contest-rank": "比赛排行",
  "discuss-list": "讨论区",
  "discuss-detail": "讨论详情",
  "rank-ac-all": "AC 排行",
  "study-view": "学习中心",
  "ai-conversation": "AI 教练 · 我的对话",
  "ai-conversation-detail": "AI 教练 · 对话详情",
  "ai-stats": "AI 教练 · 我的统计",
  "manage-ai-coach-stats": "管理 · 教练统计",
};

/** entry_page → 展示名（浮窗提示与统计分布共用） */
export function GetAiCoachPageTitle(key: string): string {
  if (!key) {
    return "";
  }
  return PAGE_TITLES[key] ?? key;
}

/** 注册一个页面入口；同 key 重复注册时后者覆盖（热更新时同一路由会重挂组件） */
export function RegisterAiCoachPageEntry(entry: AiCoachPageEntry) {
  pageEntries.set(entry.key, entry);
  aiCoachEntryVersion.value++;
}

/** 注销页面入口（组件卸载时调用，避免浮窗把用户引到一个已经不存在的页面状态） */
export function UnregisterAiCoachPageEntry(key: string) {
  if (pageEntries.get(key)?.key === key) {
    pageEntries.delete(key);
    aiCoachEntryVersion.value++;
  }
}

/** 按路由 name 取页面入口；未注册（页面不提供教练入口）返回 undefined */
export function GetAiCoachPageEntry(key: string | null | undefined): AiCoachPageEntry | undefined {
  if (!key) {
    return undefined;
  }
  return pageEntries.get(key);
}

// ── 统计口径的「打开页面真实路径」（entry_path）──

/**
 * BuildAiCoachEntryPath 需要的路由信息。
 * 用结构类型而不是 RouteLocationNormalized：单测可以直接构造，不必造一个 router。
 */
export interface AiCoachEntryRouteLike {
  /** 路由 name；404 兜底路由不记路径 */
  name?: RouteRecordName | null;
  /** 已替换路由参数的路径（如 /problem/P1001），不含 query 与 hash */
  path: string;
  /** 查询参数（只取参数名，取值一律丢弃） */
  query?: LocationQuery;
}

/**
 * 生成上报给统计的「打开页面真实路径」。
 *
 * 刻意**不用 route.fullPath**：query 的取值里会出现用户输入（评测列表的 username、
 * 登录跳转的 redirect_uri、搜索词等），一旦落库就会在管理端以聚合 URL 的形式可见。
 * 这里只保留 query 的**参数名**——参数名由代码定义、取值才是用户数据——于是
 * 「用户是从带筛选的列表页进来的」这层入口形状留下了，用户输入不可能泄露。
 *
 * 404 兜底路由返回空串：那一串是地址栏里的任意文本（可能是用户粘贴的内容），没有统计价值。
 * 长度由后端按列宽截断（ai_conversation.entry_path 为 varchar(256)），前端不重复截断。
 */
export function BuildAiCoachEntryPath(route: AiCoachEntryRouteLike): string {
  if (route.name === "404") {
    return "";
  }
  // 参数名排序后拼接：同一组参数无论书写顺序如何都归到同一个统计桶
  const queryKeys = Object.keys(route.query ?? {}).sort();
  if (queryKeys.length === 0) {
    return route.path;
  }
  return `${route.path}?${queryKeys.join("&")}`;
}
